import React, {useState, useEffect, useRef} from 'react';
import ActionItemCommentFiles from './ActionItemCommentFiles.mjs';
import ActionItemCommentEditForm from './ActionItemCommentEditForm.mjs';
import FilePicker from './FilePicker.mjs';
import "./ActionItemCommentListItem.css";
import { useCTAL } from './CTAL_Hooks.js';

const DEBUG = false;

export default function ActionItemCommentListItem({comment}) 
{
    const {app_user, team_member_role, action_item, action_item_comment_reactions, setSelectedActionItem, setActionItemDisplayRefresh} = useCTAL();

    const [like, setLike] = useState(false);
    const [dislike, setDisLike] = useState(false);
    const [like_icon, setLikeIcon] = useState("./images/like.svg");
    const [dislike_icon, setDisLikeIcon] = useState("./images/dislike.svg");

    const [file_picker_display, setFilePickerDisplay] = useState(<></>);
    const [show_file_picker, setShowFilePicker] = useState(false);

    const [comment_display, setCommentDisplay] = useState(<></>);
    const [show_edit_comment, setShowEditComment] = useState(false);
    
    const initRef = useRef();
    
    const tooltipRef_edit   = useRef(null);
    const tooltipRef_delete = useRef(null);

    useEffect(() => {
        const tooltip_edit   = new window.bootstrap.Tooltip(tooltipRef_edit.current, { container: 'body', trigger: 'hover' });
        const tooltip_delete = new window.bootstrap.Tooltip(tooltipRef_delete.current, { container: 'body', trigger: 'hover' });
  
        return () => {
            tooltip_edit.dispose();
            tooltip_delete.dispose();
        };
    }, []);

    var likes_label    = (comment.NumLikes == 1 ? "like" : "likes");
    var dislikes_label = (comment.NumDisLikes == 1 ? "dislike" : "dislikes");
    
    //10/4/2023 Changed to CommentTxt because Comment is a reserved keyword
    var comment_txt;
    
    useEffect(() =>
    {
        if (!comment) return;
        
        if (!comment.CommentTxt) comment_txt = comment.Comment;
        else comment_txt = comment.CommentTxt;
        
        setShowEditComment(false);
        setCommentDisplay(<> <small><i>{comment_txt}</i></small> </>);

        if (DEBUG) console.log(`ActionItemCommentListItem - name: ${comment.Name}, comment: ${comment.CommentTxt}, created by: ${comment.CreatedBy}, app_user: ${app_user.UserFullName}`);

        //Only show edit and delete icons if current app_user is the creator of the comment OR App User is a Team Admin, else hide
        var visibility_txt = "hidden";
        if ((comment.CreatedBy == app_user.UserFullName) || (team_member_role == "Admin")) visibility_txt = "visible";

        document.getElementById(comment.Name+"-modifiers").style.visibility = visibility_txt;
    }, [comment]);
    
    useEffect(() =>
    {
        if (!comment) return;
        if (show_file_picker)
            setFilePickerDisplay(<div class="mt-2"><FilePicker home_team_id={action_item.HomeTeamID} target_type={comment.Type} target_id={comment.CommentID} refresh_target = {refresh_comment} picker_prefix={picker_prefix}/></div> );
        else
            setFilePickerDisplay(<></>);
    }, [ show_file_picker ]);
    
    useEffect(() =>
    {
        if (DEBUG) console.log(`ActionItemCommentListItem - show_edit_comment: ${show_edit_comment}`);

        if (!comment) return;
        if (show_edit_comment)
            setCommentDisplay(<ActionItemCommentEditForm comment={comment} />)
        else {
            if (!comment.CommentTxt) comment_txt = comment.Comment;
            else comment_txt = comment.CommentTxt;
            
            setCommentDisplay(<> <small><i>{comment_txt}</i></small> </>);
        }
    }, [ show_edit_comment ]);

    useEffect(() =>
    {
        set_reaction_icons();
    }, [ action_item ]);

    useEffect(() =>
    {
        if (like) {
            setLikeIcon("./images/like-sel.jpg");
            setDisLikeIcon("./images/dislike.svg");
        }
        else if (dislike) {
            setLikeIcon("./images/like.svg");
            setDisLikeIcon("./images/dislike-sel.jpg");
        }
        else { //both are false
            setLikeIcon("./images/like.svg");
            setDisLikeIcon("./images/dislike.svg");
        }
    }, [ like, dislike ]);

    var CreatedDT = new Date(comment.CreatedAt);

    const formattedDate = CreatedDT.toLocaleString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
    });
   
    const set_reaction_icons = () => { 
        for (var i=0; i < action_item_comment_reactions.length; i++) {
            if ((action_item_comment_reactions[i].TargetID == comment.CommentID) && (action_item_comment_reactions[i].UserName == app_user.UserName)) {
                if (action_item_comment_reactions[i].ReactionType == "Like") {
                    setLike(true);
                    setDisLike(false);
                }
                else if (action_item_comment_reactions[i].ReactionType == "DisLike") {
                    setLike(false);
                    setDisLike(true);
                }

                if (DEBUG) console.log(`ActionItemCommentListItem - FOUND Reaction: ${action_item_comment_reactions[i].ReactionType}`);
                return;
            }
        }

        if (DEBUG) console.log(`ActionItemCommentListItem - Reaction NOT FOUND for ${comment.Name}`);
        setLike(false);
        setDisLike(false);
    }

    const refresh_comment = () => { 
        setSelectedActionItem(action_item); //Keep this Action Item Detail Displayed
        setActionItemDisplayRefresh(true);  //Trigger the Action-Item bulk download in ActionItem.mjs
    }
    
    //This breaks design of only updating UI on DB result round-trip, but Eric and Bill both complained 
    const update_local_reaction = (reaction_type) => { 
        switch (reaction_type)
        {
            case "Like":
                setLike(!like);
                if (dislike) setDisLike(false);
                break;
            case "DisLike":
                setDisLike(!dislike);
                if (like) setLike (false);
                break;
        }
    }

    const create_reaction = (reaction_type) => { 
        if (DEBUG) console.log(`ActionItemCommentListItem - Received Reaction: ${reaction_type}`);

        update_local_reaction(reaction_type);  //This breaks design of only updating UI on DB result round-trip, but Eric and Bill both complained 

        var type = "create_reaction";
        var target_type     = comment.Type;
        var target_pk       = comment.PK;
        var target_sk       = comment.SK;
        var user_name       = app_user.UserName;
        var user_full_name  = app_user.UserFullName;

        var body = JSON.stringify({type, reaction_type, target_type, target_pk, target_sk, user_name, user_full_name});
        
        console.log(`ActionItemCommentListItem - Creating new Comment Reaction (${reaction_type}) via ctal-set for: ${comment.Name}`);
        //SSS-FUNC fetch(`https://cpcsaczyec.execute-api.us-east-1.amazonaws.com/ctal-set`, { method: "POST", body: body })
        fetch(`https://cpcsaczyec.execute-api.us-east-1.amazonaws.com/ctal-func`, { method: "POST", body: body })
            .then(response => response.json())
            .then(() => refresh_comment());
            //.catch(setError);
    }

    const attach_files = () => { 
        setShowFilePicker(!show_file_picker);
    }
    
    const edit_comment = () => { 
        setShowEditComment(!show_edit_comment);
    }

    const delete_comment = () => {         
        var type            = "delete_comment_ui";
        var comment_id      = comment.CommentID;
        var user_full_name  = app_user.UserFullName;

        var body = JSON.stringify({type, comment_id, user_full_name});
        
        console.log(`ActionItemCommentListItem - Delete Comment (${comment.Name}) via ctal-upd`);
        //SSS-FUNC fetch(`https://cpcsaczyec.execute-api.us-east-1.amazonaws.com/ctal-upd`, { method: "POST", body: body })
        fetch(`https://cpcsaczyec.execute-api.us-east-1.amazonaws.com/ctal-func`, { method: "POST", body: body })
            .then(response => response.json())
            .then(() => refresh_comment());
            //.catch(setError);
    }

    if ((comment) && (initRef.id != comment.CommentID))
    {
        initRef.id = comment.CommentID;

        if (!comment.CommentTxt) comment_txt = comment.Comment;
        else comment_txt = comment.CommentTxt;
        
        setCommentDisplay(<> <small><i>{comment_txt}</i></small> </>);       
    }

    const picker_prefix = comment.CommentID;

    return (
        <>
            <li class="list-group-item mt-2 ">
                <div>
                    <div class="row">
                        <div class="col-9 col-lg-9" >
                            <small><b>{comment.CreatedBy}</b> commented on {formattedDate}</small>
                        </div>
                        <div class="col-3 col-lg-3 text-end">
                            <div id={comment.Name+"-modifiers"}>
                                <a href="#" ref={tooltipRef_edit}   data-bs-toggle="tooltip" data-bs-title="Edit Comment"   data-bs-delay="750"><img class="list_item_icon" src="./images/form.svg"  alt="edit"   border="0" onClick={edit_comment}/></a>
                                <a href="#" ref={tooltipRef_delete} data-bs-toggle="tooltip" data-bs-title="Delete Comment" data-bs-delay="750"><img class="list_item_icon-end" src="./images/trash.svg" alt="delete" border="0" onClick={delete_comment}/></a>                                
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            {comment_display}
                        </div>
                    </div>

                    <div class="row mt-2">
                        <div class="col-12 text-end">
                            <small>{comment.NumLikes} {likes_label}, {comment.NumDisLikes} {dislikes_label}</small>
                        </div>
                    </div>

                    <div class="row botton-comment-row">
                        <div class="col attach-comment-file">
                            <a href="#" onClick={attach_files}><img class="list_item_icon" src="./images/file.svg" alt="file" border="0"/>Attach Files</a>                     
                        </div>
                        <div class="col-4 col-lg-5 text-end me-4">
                            <a href="#"><img class="list_item_icon" src={like_icon}    alt="like"    border="0" onClick={ () => create_reaction("Like") }/></a> 
                            <a href="#"><img class="list_item_icon" src={dislike_icon} alt="dislike" border="0" onClick={ () => create_reaction("DisLike") }/></a> 
                        </div>
                    </div>

                    {file_picker_display}

                    <ActionItemCommentFiles comment={comment} margin_top={"mt-0"} /> 

                </div>
            </li>
        </>
    );
}